import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import SpecBuilder from "csi/core/SpecBuilder";
import csi from "csi/main";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { ATTR_MODIFIED_AT, ATTR_MODIFIED_BY, ATTR_RECENT_CHILD_COMMENTS, ATTR_RECENT_COMMENTS } from "csi/reflection/reflectionConstants";
import ReflectionDomainClass from "csi/reflection/ReflectionDomainClass";
import { AttributeGroup, IReflectionDomainAttribute } from "csi/reflection/reflectionTypes";
import reflectionHints from "csi/reflectionHints";
import { each, groupBy, identity, isEqual, size, sortBy, transform, without } from "lodash";
import { ICustomViewAttribute, ICustomViewAttributeGroup } from "src/c8-platform/type/customView";
import { FORMAT_IMAGE } from "src/c8-platform/type/format";
import { ATTR_NAME, ATTR_PARENT, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeKey } from "src/c8-platform/type/value";
import { ICustomViewOptions } from "../customView/options/CustomViewOptions";

export type OptionalAttributesMaps = Record<string, Record<C8AttributeId, boolean>>;

export type AugmentDomainKeysByTypeFunction = (keys: Key[]) => Key[];

const augmentDomainKeysByType: Record<C8NodeType, AugmentDomainKeysByTypeFunction> = {};

/** register function to augment the list of custom view keys by node type */
export function registerAugmentDomainKeysByTypeFunction(type: C8NodeType, f: AugmentDomainKeysByTypeFunction): void {
	augmentDomainKeysByType[type] = f;
}

/** return extended path to attribute from the given source path */
export function getDomainAttributeExtendedPath(dc: ReflectionDomainClass, da: IReflectionDomainAttribute, sourcePath: ExtendedPath): ExtendedPath {
	const { domainId, secondaryId } = da;

	if (secondaryId) {
		const secondaryClass = dc.getDomainAttributeClass(domainId);
		return sourcePath.clone().addSecondaryRelationStep(secondaryId, secondaryClass.type);
	}

	return sourcePath;
}

/** add key to domain class keys */
export function addKeyToDomainClassKeys(
	keys: Key[],
	id: C8AttributeId,
	extendedPath: ExtendedPath,
	matrix?: string,
	allowImageKeys?: boolean,
	omitImages?: boolean,
	optionalAttributesMaps?: OptionalAttributesMaps,
): void {
	// add key and images key, if desired, to the list of keys
	const key = new Key({ id, extendedPath, matrix });
	const { reflectionAttribute } = key;

	// omit if not enabled (or attribute does not exist - it always should)
	if (!reflectionAttribute?.isEnabled) {
		return;
	}

	// if optional and not included in the map, then omit
	const optionalAttributes = optionalAttributesMaps?.[extendedPath.value];
	if (optionalAttributes && !optionalAttributes[id] && reflectionAttribute.isOptional) {
		return;
	}

	if (omitImages && (reflectionAttribute.m_format === FORMAT_IMAGE)) {
		return;
	}

	keys.push(key);

	if (allowImageKeys) {
		const { imagesKey } = key;
		if (imagesKey) {
			keys.push(imagesKey);
		}
	}
};

/** return sorted list of attribute groups and their keys available from (domain) class at end of given path */
export function getAttributeGroupKeys(
	group: AttributeGroup,
	groupPath: ExtendedPath,
	matrix?: string,
	allowImageKeys?: boolean,
	omitImages?: boolean,
	optionalAttributesMaps?: OptionalAttributesMaps,
): Key[] {
	const groupKeys = [];

	group.keys.forEach(({ id, path }) => {
		addKeyToDomainClassKeys(groupKeys, id, groupPath.plus(path), matrix, allowImageKeys, omitImages, optionalAttributesMaps);
	});

	return groupKeys;
}

/** return sorted list of attribute groups along with their path */
export function getAttributeGroupsForPath(extendedPath: ExtendedPath): [AttributeGroup, ExtendedPath][] {
	const { endClass, endDomainClass } = extendedPath;
	const groups: [AttributeGroup, ExtendedPath][] = [];

	// add any groups from end class of path
	endClass.attributeGroups.forEach((group) => {
		groups.push([group, extendedPath]);
	});

	if (endDomainClass) {
		const { domainPath } = extendedPath;

		// if a domain object, add groups from secondary classes
		each(endDomainClass.secondaries, ({ attributeGroups, type }, id) => {
			if (attributeGroups) {
				const secondaryPath = domainPath.clone().addSecondaryRelationStep(id, type);

				attributeGroups.forEach((group) => {
					groups.push([group, secondaryPath]);
				});
			}
		});
	}

	return sortBy(groups, ([group]) => group.name);
}

export function getDomainClassAttributes(
	sourcePath: ExtendedPath,
	matrix?: string,
	allowHidden?: boolean,
	allowImageKeys?: boolean,
	omitImages?: boolean,
	optionalAttributesMaps?: OptionalAttributesMaps,
	derivedFrom?: ReflectionClass,
	isRelative?: boolean,
	modifiedSet: Set<string> = new Set<string>([ATTR_MODIFIED_AT, ATTR_MODIFIED_BY]),
	addKeyFunction: Function = addKeyToDomainClassKeys,
): ReflectionAttribute[] {
	const keys = getDomainClassKeys(sourcePath, matrix, allowHidden, allowImageKeys, omitImages, optionalAttributesMaps, derivedFrom, isRelative);
	return keys.map((key) => key.reflectionAttribute);
}

/** get domain class keys for a given source path */
export function getDomainClassKeys(
	sourcePath: ExtendedPath,
	matrix?: string,
	allowHidden?: boolean,
	allowImageKeys?: boolean,
	omitImages?: boolean,
	optionalAttributesMaps?: OptionalAttributesMaps,
	derivedFrom?: ReflectionClass,
	isRelative?: boolean,
	modifiedSet: Set<string> = new Set<string>([ATTR_MODIFIED_AT, ATTR_MODIFIED_BY]),
	addKeyFunction: Function = addKeyToDomainClassKeys,
): Key[] {
	const { domainPath, endClass, endDomainClass, endType } = sourcePath;
	const omitSet = new Set<string>();
	const hasCommentPaths = reflectionHints.hasCommentPaths(endType);
	const keys: Key[] = [];
	const augmentFunction = augmentDomainKeysByType[endType] ?? identity;

	// if the "__Parent__" attribute is wanted in the view, then it must be added explicitly to the view;
	// otherwise, just use the Node Name attribute of the parent class; it is allowed in Export views and Custom View Filtering though using
	// the allowHidden option
	if (!allowHidden && !isRelative) {
		omitSet.add(ATTR_PARENT);
	}

	// use one or the other based on whether or not comment paths are configured for the class
	omitSet.add(hasCommentPaths ? ATTR_RECENT_COMMENTS : ATTR_RECENT_CHILD_COMMENTS);

	// abstract class should include name attribute, so not needed for all derived classes
	if (derivedFrom) {
		omitSet.add(ATTR_NAME);
	}

	if (endDomainClass) {
		// add specific private attributes from secondary classes to omit set
		reflectionHints.getPrivateSecondaryAttributes(endType)?.forEach((id) => {
			omitSet.add(id);
		});

		const domainAttributes = endDomainClass.getDomainAttributes();
		const domainRelations = endDomainClass.getDomainRelations();

		[...domainAttributes, ...domainRelations].forEach((da) => {
			const { attributeId, isEnabled, isHidden, ownerType } = da.rca;

			if (isEnabled &&
				(allowHidden || !isHidden) &&
				!modifiedSet.has(attributeId) &&
				!omitSet.has(attributeId) &&
				!derivedFrom?.isDerivedFrom(ownerType)
			) {
				const attributePath = getDomainAttributeExtendedPath(endDomainClass, da, domainPath);

				// when we show concrete class (i.e. Style) attributes derived from some abstract class (i.e. BaseProduct), make sure the keys
				// specify the real owner class (i.e. Product). That way two concrete classes can still share the same key even when attribute is
				// not on the base abstract class
				if (derivedFrom && (ownerType !== attributePath.endType)) {
					// commented out to prevent the duplicate issue in grouping store dropdown selection. It was caused by spec.keys / uniqueId showing the abstract class instead of concrete class
					// attributePath = attributePath.clone().setEndType(ownerType);
				}

				if (!attributePath.isOmittedSubtypeSecondary) {
					addKeyFunction(keys, attributeId, attributePath, matrix, allowImageKeys, omitImages, optionalAttributesMaps);
				}
			}
		});
	} else {
		// handle just reflection class (such as DataSheet); the assumption is that there are not optional attributes for abstract classes
		endClass.getAttributes(true, true, false, derivedFrom)
			.forEach((ra) => {
				const { attributeId, isEnabled, isHidden, secondary } = ra;

				// secondary attributes like "Attributes", "ProductSourcing", etc. are omitted - even though these are
				// attributes on a base class, we know they are secondary because they are noted as such on all derived classes
				if (isEnabled &&
					(allowHidden || !isHidden) &&
					!secondary &&
					(!modifiedSet.has(attributeId) || isRelative) &&
					!omitSet.has(attributeId)
				) {
					addKeyFunction(keys, attributeId, domainPath, matrix, allowImageKeys, omitImages);
				}
			});
	}

	return augmentFunction(keys);
}

export class CustomViewAttributeMap {
	private readonly forHiddenKeys: boolean;
	private readonly mapKeys = new Map<string, ICustomViewAttribute>();
	private groups: ICustomViewAttributeGroup[] = [];

	/** return true if any group contains the passed key */
	containKeysInGroup = (key: C8AttributeKey): boolean => {
		return this.groups.some(({ keys }) => keys.some((x) => x === key));
	};

	/** return attribute for given id */
	get = (id: string): ICustomViewAttribute => {
		return this.mapKeys.get(id);
	};

	/** return attribute key for given id */
	getKey = (id: string): C8AttributeKey | undefined => {
		const groupOrKey = this.mapKeys.get(id);
		if (typeof groupOrKey === "string") {
			return groupOrKey;
		}
	};

	/** return attribute group for given id */
	getGroup = (id: string): ICustomViewAttributeGroup | undefined => {
		const groupOrKey = this.mapKeys.get(id);
		if (typeof groupOrKey !== "string") {
			return groupOrKey;
		}
	};

	/** return true if key is in map; if map stores hidden keys, then only return true for groups if all child keys are hidden */
	has = (groupOrKey: ICustomViewAttribute, group?: ICustomViewAttributeGroup): boolean => {
		const { forHiddenKeys, id, mapKeys } = this;

		if (!groupOrKey) {
			return false;
		}

		return mapKeys.has(id(groupOrKey, group)) || (forHiddenKeys && group && mapKeys.has(id(group)));
	};

	/** return unique id for attribute and parent group if specified*/
	id = (groupOrKey: ICustomViewAttribute, group?: ICustomViewAttributeGroup): string => {
		let id: string;

		if (typeof groupOrKey === "string") {
			id = groupOrKey;

			// matrix group keys are already unique, but not so for attribute group keys
			if (group && !group.matrixTarget) {
				id = `${id}--${group.groupKey || group.name}`;
			}
		} else {
			const { groupKey, matrixTarget, name } = groupOrKey;

			if (matrixTarget) {
				id = `${groupKey}{${matrixTarget}}`;
			} else {
				id = groupKey || name;
			}
		}

		return id;
	};

	constructor(keys: ICustomViewAttribute[], forHiddenKeys?: boolean, ignoreGrouping?: boolean) {
		const { id, mapKeys } = this;

		this.forHiddenKeys = forHiddenKeys;

		if (keys) {
			for (const groupOrKey of keys) {
				const uniqueId = id(groupOrKey);
				const group = groupOrKey as ICustomViewAttributeGroup;
				const groupKeys = group?.keys;

				// add key unless creating map for hiddenKeys and the group has child keys (which means the whole group is not hidden)
				if (!forHiddenKeys || !groupKeys || groupKeys.length === 0) {
					mapKeys.set(uniqueId, groupOrKey);
				}

				if (groupKeys) {
					this.groups.push(group);

					for (const groupKey of groupKeys) {
						const groupedId = id(groupKey, ignoreGrouping ? null : group);
						mapKeys.set(groupedId, groupKey);
					}
				}
			}
		}
	}
}

/** return flattened list of all attribute keys, containing no groups (other than matrix group keys); omit attribute group keys if so desired */
export function flattenAttributeKeys(attributeKeys: ICustomViewAttribute[], omitAttributeGroupKeys?: boolean): C8AttributeKey[] {
	return attributeKeys.flatMap((groupOrKey) => {
		if (typeof groupOrKey === "string") {
			return groupOrKey;
		} else {
			const { groupKey, keys, matrixTarget } = groupOrKey;
			return (groupKey && (matrixTarget || !omitAttributeGroupKeys)) ? [groupKey, ...keys] : keys;
		}
	});
}

/** return first key in the list of given keys, making sure to handle a group being first in the list */
export function getFirstAttributeKey(attributeKeys: ICustomViewAttribute[]): C8AttributeKey | undefined {
	const firstKey = attributeKeys[0];
	if (firstKey) {
		return (typeof firstKey === "string") ? firstKey : firstKey.keys[0];
	}
}

/** return typed key string if key is found within given keys */
export function findAttributeKey(keys: ICustomViewAttribute[], searchKey: Key | C8AttributeKey, omitGroups?: boolean): C8AttributeKey | undefined {
	const typedKey = searchKey.toString();
	const found = keys.some((groupOrKey) => {
		if (typeof groupOrKey === "string") {
			return groupOrKey === typedKey;
		} else {
			return (groupOrKey.groupKey === typedKey) || (!omitGroups && groupOrKey.keys.includes(typedKey));
		}
	});

	if (found) {
		return typedKey;
	}
}

/** return typed key string if untyped key is found within given keys */
export function findUntypedAttributeKey(keys: ICustomViewAttribute[], searchKey: Key | C8AttributeKey, omitGroups?: boolean): C8AttributeKey | undefined {
	const untypedKey = (typeof searchKey === "string") ? searchKey : searchKey.untypedString;
	let resultKey: C8AttributeKey;

	keys.some((groupOrKey) => {
		if (typeof groupOrKey === "string") {
			if (Key.untypedKey(groupOrKey) === untypedKey) {
				resultKey = groupOrKey;
			}
		} else if (Key.untypedKey(groupOrKey.groupKey) === untypedKey) {
			resultKey = groupOrKey.groupKey;
		} else if (!omitGroups) {
			groupOrKey.keys.some((groupedKey) => {
				if (Key.untypedKey(groupedKey) === untypedKey) {
					resultKey = groupedKey;
					return true;
				}
			});
		}

		return !!resultKey;
	});

	return resultKey;
}

/** return system attribute groups configured on Business Objects at end of given path */
function getSystemAttributeGroupsForPath(extendedPath: ExtendedPath): ICustomViewAttributeGroup[] {
	return extendedPath.endClass.attributeGroups.map(({ name, keys }) => {
		const typedKeys = keys.map(({ id, path }) => {
			const key = new Key({
				id,
				extendedPath: extendedPath.plus(path),
			});

			return key.typedString;
		});

		return {
			groupKey: Key.getAttributeGroupKey(name, extendedPath).typedString,
			keys: typedKeys,
			name,
		};
	});
}

/** return filtered list of visible attribute keys taking into account the given hidden keys */
export function getVisibleAttributeKeys(attributeKeys: ICustomViewAttribute[], hiddenKeys: ICustomViewAttribute[], skipKeysSet?: Set<C8AttributeKey>): ICustomViewAttribute[] {
	const hiddenMap = new CustomViewAttributeMap(hiddenKeys, true);
	const testSkipSet = !!skipKeysSet?.size;

	// test both typed and untyped keys when checking skipKeysSet
	const allow = (key: C8AttributeKey): boolean => {
		return !testSkipSet || (!skipKeysSet.has(key) && !skipKeysSet.has(Key.untypedKey(key)));
	};

	return transform(attributeKeys, (result, groupOrKey) => {
		if (!hiddenMap.has(groupOrKey)) {
			if (typeof groupOrKey === "string") {
				if (allow(groupOrKey)) {
					result.push(groupOrKey);
				}
			} else {
				const { groupKey, keys, ...otherProps } = groupOrKey;

				if (!groupKey || allow(groupKey)) {
					const groupedKeys = keys.filter((key) => {
						return allow(key) && !hiddenMap.has(key, groupOrKey);
					});

					if (groupedKeys.length) {
						result.push({
							groupKey,
							keys: groupedKeys,
							...otherProps,
						});
					}
				}
			}
		}
	}, []);
}

/** convert flat lists of attribute keys grouped by Business Object Attribute Groups into new grouped structure */
function migrateKeysV791(
	ungroupedKeys: ICustomViewAttribute[],
	attributeGroups: ICustomViewAttributeGroup[],
	groupedKeys: Record<C8AttributeKey, C8AttributeKey[]>,
): ICustomViewAttribute[] {
	if (!ungroupedKeys.length) {
		return ungroupedKeys;
	}

	// step through all available headings and assign them according to the group name
	const flatKeys: ICustomViewAttribute[] = [];
	const groupedKeysInView: Record<C8AttributeKey, C8AttributeKey[]> = {};

	ungroupedKeys.forEach((key) => {
		if (typeof key === "string") {
			const normalizedKey = Key.referencedImagesBaseKey(key) || key;
			const groupKeys = groupedKeys[normalizedKey];
			if (groupKeys) {
				groupKeys.forEach((groupKey) => {
					groupedKeysInView[groupKey] = (groupedKeysInView[groupKey] || []).concat(key);
				});
			} else {
				flatKeys.push(key);
			}
		}
	});

	// populate attribute groups according to order in the Attribute Group definition
	attributeGroups.forEach(({ groupKey, keys, name }) => {
		const groupKeysInView = groupedKeysInView[groupKey];
		if (groupKeysInView) {
			// sort keys and insert groups
			const indexMap = csi.hashArray(keys, null, Number);
			const sortedKeys = sortBy(groupKeysInView, (key) => {
				// need to handle special reflist to image container headers specially - they should appear immediately after
				// their corresponding heading in the group (i.e. Active Colorways, Active Colorways Images)
				const index = indexMap[key] ?? (indexMap[Key.referencedImagesBaseKey(key)] + 0.5);
				return index;
			});

			flatKeys.push({
				groupKey,
				keys: sortedKeys,
				name,
			});
		}
	});

	return flatKeys;
}

/** convert flat lists of attribute keys grouped by Business Object Attribute Groups into new grouped structure */
export function migrateAttributeKeysIntoGroups(options: ICustomViewOptions, attributeGroups: ICustomViewAttributeGroup[]): void {
	const { attributeKeys, hiddenKeys } = options;
	const groupedKeys: Record<C8AttributeKey, C8AttributeKey[]> = {};

	attributeGroups.forEach(({ groupKey, keys }) => {
		keys.forEach((key) => {
			groupedKeys[key] = (groupedKeys[key] || []).concat(groupKey);
		});
	});

	if (attributeKeys) {
		options.attributeKeys = migrateKeysV791(attributeKeys, attributeGroups, groupedKeys);
	}

	if (hiddenKeys) {
		options.hiddenKeys = migrateKeysV791(hiddenKeys, attributeGroups, groupedKeys);
	}
}

/** convert flat lists of attribute keys grouped by Business Object Attribute Groups into new grouped structure */
export function migratePropertyKeysV791(options: ICustomViewOptions, rc: ReflectionClass, optionalPaths?: string[]): void {
	const attributeGroups = [""].concat(optionalPaths || []).flatMap((untypedPath) => {
		const groupPath = ExtendedPath.create(rc, untypedPath);
		return groupPath ? getSystemAttributeGroupsForPath(groupPath) : [];
	});

	if (attributeGroups.length) {
		migrateAttributeKeysIntoGroups(options, attributeGroups);
	}
}

/** convert flat lists of attribute keys into grouped structure for matrix keys */
export function migrateTableKeysV791(
	options: Pick<ICustomViewOptions, "attributeKeys" | "hiddenKeys">,
	specBuilder: SpecBuilder,
	renamedMatrixKeys?: Record<C8AttributeKey, C8AttributeKey>,
	missingMatrixKeys?: C8AttributeKey[],
): void {
	const { attributeKeys, hiddenKeys } = options;
	const keys1 = (attributeKeys || []) as C8AttributeKey[];
	const keys2 = (hiddenKeys || []) as C8AttributeKey[];
	const keyObjects1 = keys1.map(Key.toKey);
	const keyObjects2 = keys2.map(Key.toKey);
	const mapMatrixKeys: Record<string, Key[]> = groupBy(keyObjects1, (key) => key.matrix || "");
	const mapHiddenMatrixKeys: Record<string, Key[]> = groupBy(keyObjects2, (key) => key.matrix || "");

	if (size(mapMatrixKeys) > 1) {
		// there is a matrix group, so need to migrate
		const flatKeys = mapMatrixKeys[""] || [];
		const flatHiddenKeys = mapHiddenMatrixKeys[""] || [];
		const hiddenMap = new CustomViewAttributeMap(flatHiddenKeys.map(Key.fromKey), true);
		const newAttributeKeys: ICustomViewAttribute[] = [];
		const newHiddenKeys: ICustomViewAttribute[] = [];

		const getMatrixGroup = (key: Key): ICustomViewAttributeGroup | undefined => {
			const { candidateMatrixGroupId } = key;
			const matrixKeys = mapMatrixKeys[candidateMatrixGroupId];

			if (matrixKeys) {
				const spec = specBuilder.getMatrixGroupSpecForId(candidateMatrixGroupId);
				if (spec) {
					const { matrixExtendedPath, matrixTargetClass, name, uniqueId } = spec;
					const groupKeys = matrixKeys.filter((key) => key.extendedPath.isAlongPath(matrixExtendedPath));

					if (groupKeys.length) {
						if (renamedMatrixKeys && (uniqueId !== key.toString())) {
							renamedMatrixKeys[key.originalKey] = uniqueId;
						}

						return {
							groupKey: uniqueId,
							keys: groupKeys.map(Key.fromKey),
							matrixTarget: matrixTargetClass.type,
							name,
						};
					}
				} else {
					// track matrix keys that don't exist in view
					missingMatrixKeys?.push(key.originalKey);
				}
			}
		};

		for (const key of flatKeys) {
			// some (matrix) keys used to be untyped (such as "___BOMProductSizes::-1"), so make sure to handle both typed and untyped keys
			const { candidateMatrixGroupId, typedString, untypedString } = key;
			const isHidden = hiddenMap.has(typedString) || hiddenMap.has(untypedString);
			const matrix = getMatrixGroup(key);
			const migratedKey = matrix || typedString;

			if (migratedKey) {
				newAttributeKeys.push(migratedKey);

				if (matrix) {
					const hiddenMatrixKeys = isHidden ? [] : mapHiddenMatrixKeys[candidateMatrixGroupId];

					if (hiddenMatrixKeys) {
						newHiddenKeys.push({
							...matrix,
							keys: hiddenMatrixKeys.map(Key.fromKey),
						});
					}
				} else if (isHidden) {
					newHiddenKeys.push(migratedKey);
				}
			}
		}

		options.attributeKeys = newAttributeKeys;
		options.hiddenKeys = newHiddenKeys;
	}
}

/** add prefix path to each key; note that this should not be used with matrices */
export function prefixAttributeKeys(attributeKeys: ICustomViewAttribute[], prefixPath: ExtendedPath): ICustomViewAttribute[] {
	const addPrefix = (dpkey: C8AttributeKey): C8AttributeKey => {
		const key = new Key({ dpkey });
		const prefixedKey = key.getKeyAlongPath(prefixPath);
		return prefixedKey.typedString;
	};

	return attributeKeys.map((groupOrKey) => {
		if (typeof groupOrKey === "string") {
			return addPrefix(groupOrKey);
		} else {
			const { keys, name } = groupOrKey;
			return {
				keys: keys.map(addPrefix),
				name,
			};
		}
	});
}

/** extract matrix path (and matrix designation) from the given key */
export function removeMatrixFromAttributeKey(subKey: C8AttributeKey, matrixDepth: number): C8AttributeKey {
	const oldKey = Key.toKey(subKey);
	const extendedPath = oldKey.extendedPath.clone();

	console.assert(!!oldKey.matrix);
	console.assert(oldKey.extendedPath?.depth >= matrixDepth);

	extendedPath.shiftSteps(matrixDepth, true);

	const newKey = oldKey.copy({
		extendedPath,
		matrix: null,
	});
	return newKey.typedString;
}

/** validate that all attribute keys are not hidden and that the first un-hidden attribute is not part of a group (attribute or matrix) */
export function validateAttributeAndHiddenKeys(
	attributeKeys: ICustomViewAttribute[],
	hiddenKeys?: ICustomViewAttribute[],
	flatKeyToInsertIfGroupFirst?: C8AttributeKey,
): [ICustomViewAttribute[], ICustomViewAttribute[]] {
	if (attributeKeys && flatKeyToInsertIfGroupFirst) {
		// validate that first visible attribute key exists and is not in a group
		const hiddenMap = new CustomViewAttributeMap(hiddenKeys, true);
		let firstKey: C8AttributeKey;

		attributeKeys.some((groupOrKey) => {
			if (typeof groupOrKey === "string") {
				if (!hiddenMap.has(groupOrKey)) {
					// there is a visible attribute key before any group, so just return
					return true;
				}

				// track first flat hidden key
				firstKey ||= groupOrKey;
			} else if (!hiddenMap.has(groupOrKey)) {
				if (!firstKey) {
					// if no flat key found before group, insert default key from view
					attributeKeys = without(attributeKeys, flatKeyToInsertIfGroupFirst);
					attributeKeys.unshift(flatKeyToInsertIfGroupFirst);
				}

				// remove first key from hidden keys
				if (hiddenKeys) {
					hiddenKeys = without(hiddenKeys, firstKey || flatKeyToInsertIfGroupFirst);
				}

				return true;
			}
		});

		// view must have at least one non hidden attribute
		if (isEqual(attributeKeys, hiddenKeys)) {
			hiddenKeys = without(hiddenKeys, firstKey || flatKeyToInsertIfGroupFirst);
		}
	}

	return [attributeKeys, hiddenKeys];
}

/** remove invalid keys from the given key map */
export function validateAttributeKeyMap(keys: Record<string, C8AttributeKey>): Record<string, C8AttributeKey> {
	const validatedKeys = {};

	each(keys, (value, key) => {
		try {
			// some keys may be valid even when untyped, such as old matrix keys (___Matrix::-1); allow them through so they can be migrated
			const keyObj = Key.toKey(value);
			const keyString = keyObj.toString();
			if (keyString) {
				validatedKeys[key] = keyString;
			}
		} catch (ex) {
			// omit bad keys
		}
	});

	return validatedKeys;
}

/** For personalized views, update attributeKeys and hiddenKeys options to stay in sync with sourceView */
export function resolveAttributeKeysBasedOnSourceView(sourceAttributeKeys: ICustomViewAttribute[], sourceHiddenKeys: ICustomViewAttribute[], attributeKeys: ICustomViewAttribute[], hiddenKeys: ICustomViewAttribute[]): [boolean, ICustomViewAttribute[], ICustomViewAttribute[]] {
	const attributeKeyMap = new CustomViewAttributeMap(attributeKeys);
	const hiddenKeyMap = new CustomViewAttributeMap(hiddenKeys, true);
	const sourceAttributeKeyMap = new CustomViewAttributeMap(sourceAttributeKeys);
	let ret = false;

	const keysToRemoveFromAttributeKeys: ICustomViewAttribute[] = [];

	// handle the potential keys could possibly added to the personalized view
	for (const groupOrKey of sourceAttributeKeys) {
		const isGroupKey = typeof groupOrKey !== "string";

		if (!attributeKeyMap.has(groupOrKey)) {
			if (isGroupKey) {
				// admin adds group
				const { keys } = groupOrKey;

				for (const key of keys) {
					const targetKey = attributeKeyMap.get(key);

					if (targetKey && typeof targetKey === "string") {
						// see if any attributes was in the ungrouped list, if so, remove it
						keysToRemoveFromAttributeKeys.push(targetKey);
					}
				}
			} else {
				// admin adds individual attribute to ungrouped list
				if (attributeKeyMap.containKeysInGroup(groupOrKey)) {
					// the new ungrouped attribute already exist in one group, don't add it so that it'll remain in that group
					continue;
				}
			}

			attributeKeys.push(groupOrKey);
			ret = true;
		} else {
			if (isGroupKey) {
				// for the group key, the admin could add more
				const id = sourceAttributeKeyMap.id(groupOrKey);
				const sourceGroup = sourceAttributeKeyMap.getGroup(id);
				const targetGroup = attributeKeyMap.getGroup(id);

				for (const key of sourceGroup.keys) {
					if (!targetGroup.keys.includes(key) && !attributeKeyMap.has(key)) {
						targetGroup.keys.push(key);
						ret = true;
					} else if (!targetGroup.keys.includes(key) && attributeKeyMap.has(key) && sourceAttributeKeyMap.has(key)) {
						// there's one key doesn't exist in the group of the personalized view, also, it exist in the ungrouped list, keep them in both places
						targetGroup.keys.push(key);
						ret = true;
					}
				}
			}
		}
	}

	// handle the potential keys could possibly removed from the personalized view
	for (const groupOrKey of attributeKeys) {
		const isIndividualKey = typeof groupOrKey === "string";

		if (!sourceAttributeKeyMap.has(groupOrKey)) {
			// the attribute is moved to the group, don't remove, except the case where the attribute is in both grouped and ungrouped list
			if (isIndividualKey && sourceAttributeKeyMap.containKeysInGroup(groupOrKey) && !(attributeKeyMap.containKeysInGroup(groupOrKey))) {
				continue;
			}

			keysToRemoveFromAttributeKeys.push(groupOrKey);
		}

		// handle the keys in the group
		if (!isIndividualKey) {
			const id = attributeKeyMap.id(groupOrKey);
			const targetGroup = attributeKeyMap.getGroup(id);

			targetGroup?.keys.forEach((key) => {
				if (!sourceAttributeKeyMap.has(key, targetGroup) && !sourceAttributeKeyMap.has(key)) {
					const attributeId = attributeKeyMap.id(key, groupOrKey);
					keysToRemoveFromAttributeKeys.push(attributeId);
				}
			});
		}
	}

	// handle the hidden keys in the group
	for (const [i, groupOrKey] of sourceHiddenKeys.entries()) {
		if (!hiddenKeyMap.has(groupOrKey)) {
			hiddenKeys.splice(i, 0, groupOrKey);
			ret = true;
		}
	}

	keysToRemoveFromAttributeKeys.forEach((key) => {
		const beingRemovedKeyIsIndividualKey = typeof key === "string";

		attributeKeys = attributeKeys.filter((groupOrKey) => {
			const currentKeyIsIndividualKey = typeof groupOrKey === "string";
			if (beingRemovedKeyIsIndividualKey && currentKeyIsIndividualKey) {
				return groupOrKey !== key;
			} else if (!beingRemovedKeyIsIndividualKey && !currentKeyIsIndividualKey) {
				return groupOrKey.groupKey !== (key as ICustomViewAttributeGroup).groupKey;
			} else if (beingRemovedKeyIsIndividualKey && !currentKeyIsIndividualKey) {
				// key in the group needs to be removed
				groupOrKey.keys = groupOrKey.keys.filter((keyInGroup) => attributeKeyMap.id(keyInGroup, groupOrKey) !== key);
			}

			return true;
		});

		ret = true;
	});

	return [ret, attributeKeys, hiddenKeys];
}